import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { applicationDevicesQuery, applicationSummaryQuery, applicationsTableQuery, type AppDoor } from '../api/softsee'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import { failureText } from '../alarms/AlarmDetails'
import { UninstallApplicationDevices, UpdateApplicationDevices } from './ApplicationDeviceWrites'
import { ApplicationDevicesTab } from './ApplicationDevicesTab'
import { applicationColumns } from './columns'

/**
 * Softsee::applications - `#softseeApplications`. Expand: Summary, Devices,
 * Uninstall, Update.
 *
 * Uninstall si Update SCRIU, ca in portalul vechi. Statea aici un formular
 * mut, cu un buton care nu trimitea nimic, desi placile de dezinstalare si de
 * actualizare erau deja scrise si probate: ecranul aplicatiei arata cine o are
 * instalata, dar nu se putea face nimic cu asta.
 */

type Tab = 'summary' | 'devices' | 'uninstall' | 'update'

/**
 * odPanelFailure(xhr) din general.js, aceeasi placa ca in `AlarmDetails` si
 * `LicenseExpand` - celelalte doua panouri cu file din randul desfacut.
 *
 * Fila care nu se incarca SPUNE ca nu s-a incarcat. Pana pe 10 septembrie 2026
 * scria aici `summary.data ? <SummaryTab/> : null`: cand capatul raspundea 404
 * - si raspundea, fiindca interogarea cerea o coloana inexistenta - fila se
 * desena GOALA. "Pe expand primul tab nu merge nu apare nimic pe el": nu era un
 * ecran care nu s-a scris, era o eroare desenata ca tacere.
 */
function Failure({ error }: { error: unknown }) {
  return (
    /* `row` in jurul coloanei: `col-md-12` e `float: left` in Bootstrap 3, iar
       `.details_content` nu are `clearfix`. Fara rand, panoul ramanea de
       inaltimea filelor si alerta ii atarna pe dedesubt. Randurile filei
       Summary sunt scrise la fel. */
    <div className="row">
      <div className="col-md-12">
        <div className="alert alert-danger">
          <strong>This did not load.</strong> <span style={{ fontSize: 12 }}>{failureText(error)}</span>
        </div>
      </div>
    </div>
  )
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'devices', label: 'Devices' },
  { key: 'uninstall', label: 'Uninstall' },
  { key: 'update', label: 'Update' },
]

/**
 * Randul desfacut al unei aplicatii. Il foloseste si lista Opteemum, care e
 * ACEEASI lista - acelasi tabel, aceleasi cinci coloane, aceeasi aplicatie -
 * deschisa pe alta usa. `door` spune numai de unde se cer citirile; scrierile
 * raman ale Softsee, unde le sta si dreptul.
 */
export function ApplicationTabs({ id, door = 'softsee', allowed }: { id: number; door?: AppDoor; allowed?: string[] }) {
  /* `allowed`: filele pe care omul le are (tabList.php le punea sub
     `canAccess()`). Fara lista - usa Opteemum - raman toate, ca pana acum. */
  const shown = allowed ? TABS.filter((t) => allowed.includes(t.key)) : TABS
  const [tab, setTab] = useState<Tab>(shown[0]?.key ?? 'summary')
  const open = shown.some((t) => t.key === tab)
  const summary = useQuery({ ...applicationSummaryQuery(id, door), enabled: open && tab === 'summary' })
  /* Pe usa Softsee fila Devices e paginata pe server (`ApplicationDevicesTab`);
     lista statica a ramas numai pe usa Opteemum, care are capatul ei. */
  const devices = useQuery({ ...applicationDevicesQuery(id, door), enabled: tab === 'devices' && door !== 'softsee' })

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {shown.map((one) => (
            <li key={one.key}>
              <a
                href="#"
                className={`action_button${tab === one.key ? ' active' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(one.key)
                }}
              >
                {one.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {!open && <p className="text-muted">You do not have access to any of these details.</p>}
          {open && tab === 'summary' &&
            (summary.error ? <Failure error={summary.error} /> : summary.isPending ? <Spinner /> : summary.data ? <SummaryTab page={summary.data} /> : null)}
          {open && tab === 'devices' && door === 'softsee' && <ApplicationDevicesTab id={id} />}
          {open && tab === 'devices' && door !== 'softsee' &&
            (devices.error ? <Failure error={devices.error} /> : devices.isPending ? <Spinner /> : devices.data ? <DevicesTab rows={devices.data.data} /> : null)}
          {open && tab === 'uninstall' && <UninstallApplicationDevices id={id} />}
          {open && tab === 'update' && <UpdateApplicationDevices id={id} />}
        </div>
      </div>
    </div>
  )
}

/** applications/details/summary.php */
function SummaryTab({ page }: {
  page: {
    details: Record<string, unknown>
    installedVersions: Record<string, unknown>[]
    availableKit?: Record<string, unknown> | null
  }
}) {
  /* Primele cinci versiuni, restul la cerere: o aplicatie cu patruzeci de
     versiuni instalate umplea fila si impingea kitul de deasupra afara. */
  const [all, setAll] = useState(false)
  const versions = page.installedVersions
  const kit = page.availableKit ?? null

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <h2>{String(page.details.short_name ?? '')}</h2>
          <label className="color">
            Latest installed version found:
            <em>
              <strong> {String(page.details.last_version ?? '')}</strong>
            </em>
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          {/* Fara kit, aplicatia nu se poate actualiza de aici - si asta e
              raspunsul pe care il cauta cine deschide fila. */}
          {kit ? (
            <div className="alert alert-success">
              <span className="fas fa-cloud-download-alt"></span> <strong>Available kit:</strong>{' '}
              {String(kit.name ?? '')} version {String(kit.version ?? '')}
            </div>
          ) : (
            <div className="alert alert-warning">
              <span className="fas fa-exclamation-triangle"></span> No available kit.
            </div>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 divider">
          <h4 className="bordered">Installed Versions</h4>
        </div>
        <div className="col-md-12">
          <table className="table table-striped centered" width="100%">
            <thead>
              <tr>
                <th>#</th>
                <th className="a-left">Application version</th>
                <th>Installed devices</th>
              </tr>
            </thead>
            <tbody>
              {versions.length === 0 ? (
                <tr>
                  <td colSpan={4}>No results found.</td>
                </tr>
              ) : (
                versions.map((version, i) => (
                  <tr
                    key={i}
                    className={i > 4 ? 'hidden-outdated' : ''}
                    style={i > 4 && !all ? { display: 'none' } : undefined}
                  >
                    <td>#{i + 1}</td>
                    <td className="a-left">
                      <b>{String(version.version ?? '')} </b>
                    </td>
                    <td>
                      <span className="badge bg-warning">{String(version.devices ?? 0)}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {versions.length > 5 && !all && (
            <button className="btn btn-sm btn-danger show-all-outdated" type="button" onClick={() => setAll(true)}>
              Show All Outdated... <i className="far fa-arrow-alt-circle-down"></i>
            </button>
          )}
        </div>
      </div>
    </>
  )
}

/** Lista statica de pe usa Opteemum - cine o are instalata, si cu ce versiune. */
function DevicesTab({ rows }: { rows: Record<string, unknown>[] }) {
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse" style={{ marginBottom: 10 }}>
        <h4>
          <strong>Installed</strong> versions
        </h4>
      </header>
      {rows.length === 0 ? (
        <div className="alert alert-info">
          <span className="fas fa-info-circle"></span> No devices.
        </div>
      ) : (
        <table className="table table-striped table-hover table-bordered" width="100%" id="devicesSoftseeList">
          <thead>
            <tr>
              <th>Device</th>
              <th>User</th>
              <th style={{ width: 140 }}>Version</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={String(d.deviceId)}>
                <td>{String(d.Denumire ?? '')}</td>
                <td className="text-muted">{String(d.UserLogat ?? '')}</td>
                <td>{String(d.version ?? '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

export function ApplicationsPage() {
  const columns = useMemo(() => applicationColumns(), [])
  const [state, setState] = useTableState('softseeApplications', { start: 0, length: 25, order: { column: 2, dir: 'desc' }, search: '' })
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[2]
  const query = useQuery({
    ...applicationsTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  return (
    <Content>
      <div id="content">
        <DataTable
          id="softseeApplications"
          columnTools
          columns={columns}
          rows={query.data?.data ?? []}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          details={(row) => <ApplicationTabs id={Number(row.ID)} allowed={query.data?.tabs} />}
        />
      </div>
    </Content>
  )
}
